import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { safeNext, useAuth } from "../../auth/AuthProvider";
import { EMAIL_RE, ErrorSummary, Field, PasswordField } from "../../components/form";
import { useI18n } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/messages";
import { AuthLayout } from "./AuthLayout";
import { GoogleButton } from "./GoogleButton";

const MIN_PASSWORD = 8;

export function RegisterPage() {
  const { t } = useI18n();
  const auth = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  // "Creá tu cuenta" after buying without one comes with that order's email.
  const [form, setForm] = useState(() => ({ name: "", email: (params.get("email") ?? "").trim().slice(0, 120), password: "" }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  // Ticked by default (Ley 25.326 art. 27 allows it with an easy opt-out, which every marketing email has).
  const [marketing, setMarketing] = useState(true);
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (auth.user) return <Navigate to={next} replace />;

  if (sentTo) {
    return (
      <AuthLayout title={t("auth.checkEmailTitle")}>
        <p role="status">{t("auth.checkEmailText", { email: sentTo })}</p>
        <Link rel="nofollow" to={`/ingresar?next=${encodeURIComponent(next)}`} className="btn btn-outline btn-block">
          {t("auth.backToLogin")}
        </Link>
      </AuthLayout>
    );
  }

  const fieldError = (field: keyof typeof form, value: string): string => {
    if (field === "name") return value.trim().length >= 2 ? "" : "auth.errors.name";
    if (field === "email") return EMAIL_RE.test(value.trim()) ? "" : "auth.errors.email";
    return value.length >= MIN_PASSWORD ? "" : "auth.errors.password";
  };

  const blur = (field: keyof typeof form) => () => {
    if (form[field]) setErrors((prev) => ({ ...prev, [field]: fieldError(field, form[field]) }));
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e = Object.fromEntries(
      (Object.keys(form) as (keyof typeof form)[]).map((f) => [f, fieldError(f, form[f])]).filter(([, v]) => v),
    ) as Record<string, string>;
    setErrors(e);
    setAttempt((n) => n + 1);
    if (Object.keys(e).length) return;
    setBusy(true);
    const r = await auth.signUp(form.name.trim(), form.email.trim(), form.password, next, marketing);
    setBusy(false);
    if (r.ok) setSentTo(form.email.trim());
    else setErrors({ form: r.error });
  };

  const err = (f: string) => (errors[f] ? t(errors[f] as MessageKey) : null);

  return (
    <AuthLayout title={t("auth.registerTitle")}>
      <GoogleButton next={next} />
      <form onSubmit={submit} noValidate>
        <ErrorSummary
          errors={Object.fromEntries(Object.entries(errors).filter(([, v]) => v))}
          fieldIds={{ name: "reg-name", email: "reg-email", password: "reg-password" }}
          focusKey={attempt}
        />
        <Field id="reg-name" label={t("auth.name")} autoComplete="name" value={form.name} error={err("name")} onBlur={blur("name")} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Field
          id="reg-email"
          label={t("auth.email")}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={form.email}
          error={err("email")}
          onBlur={blur("email")}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <PasswordField
          id="reg-password"
          label={t("auth.password")}
          autoComplete="new-password"
          hint={t("auth.passwordHint")}
          value={form.password}
          error={err("password")}
          onBlur={blur("password")}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <label className="check">
          <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} /> {t("auth.marketingOptIn")}
        </label>
        <p className="muted small">
          {t("auth.acceptTerms")} <Link to="/terminos">{t("auth.termsLink")}</Link> · <Link to="/privacidad">{t("auth.privacyLink")}</Link>
        </p>
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy ? t("auth.working") : t("auth.submitRegister")}
        </button>
      </form>
      <p className="auth-switch">
        {t("auth.haveAccount")} <Link rel="nofollow" to={`/ingresar?next=${encodeURIComponent(next)}`}>{t("auth.submitLogin")}</Link>
      </p>
    </AuthLayout>
  );
}
